import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { googleAnalyticsService } from '../services/googleServices';

// Generate a unique client ID for this session
const generateClientId = () => {
  let clientId = localStorage.getItem('ga_client_id');
  if (!clientId) {
    clientId = 'client_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('ga_client_id', clientId);
  }
  return clientId;
};

export const useGoogleAnalytics = () => {
  const location = useLocation();
  const clientId = generateClientId();

  // Track page views automatically
  useEffect(() => {
    const trackPageView = async () => {
      try {
        await googleAnalyticsService.trackPageView(
          location.pathname + location.search,
          document.title,
          clientId
        );
      } catch (error) {
        console.error('Failed to track page view:', error);
      }
    };

    trackPageView();
  }, [location, clientId]);

  // Track custom events
  const trackEvent = useCallback(async (eventName, parameters = {}) => {
    try {
      await googleAnalyticsService.trackEvent(eventName, parameters, clientId);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }, [clientId]);

  // Track user login
  const trackLogin = useCallback(async (userId, method = 'email') => {
    try {
      await googleAnalyticsService.trackUserLogin(userId, method, clientId);
    } catch (error) {
      console.error('Failed to track login:', error);
    }
  }, [clientId]);

  // Track transactions
  const trackTransaction = useCallback(async (transactionId, revenue, tax = 0, shipping = 0) => {
    try {
      await googleAnalyticsService.trackTransaction(transactionId, revenue, tax, shipping, clientId);
    } catch (error) {
      console.error('Failed to track transaction:', error);
    }
  }, [clientId]);

  // Track purchases
  const trackPurchase = useCallback(async (transactionId, revenue, items = [], tax = 0, shipping = 0) => {
    try {
      await googleAnalyticsService.trackPurchase(transactionId, revenue, items, tax, shipping, clientId);
    } catch (error) {
      console.error('Failed to track purchase:', error);
    }
  }, [clientId]);

  // Common event tracking functions
  const trackButtonClick = useCallback((buttonName, page = null) => {
    trackEvent('button_click', {
      button_name: buttonName,
      page: page || location.pathname
    });
  }, [trackEvent, location.pathname]);

  const trackFormSubmission = useCallback((formName, success = true) => {
    trackEvent('form_submission', {
      form_name: formName,
      success: success,
      page: location.pathname
    });
  }, [trackEvent, location.pathname]);

  const trackSearch = useCallback((searchTerm, resultsCount = 0) => {
    trackEvent('search', {
      search_term: searchTerm,
      results_count: resultsCount,
      page: location.pathname
    });
  }, [trackEvent, location.pathname]);

  const trackProductView = useCallback((productId, productName, category = null) => {
    trackEvent('product_view', {
      product_id: productId,
      product_name: productName,
      category: category,
      page: location.pathname
    });
  }, [trackEvent, location.pathname]);

  const trackAddToCart = useCallback((productId, productName, quantity = 1, price = 0) => {
    trackEvent('add_to_cart', {
      product_id: productId,
      product_name: productName,
      quantity: quantity,
      price: price,
      page: location.pathname
    });
  }, [trackEvent, location.pathname]);

  const trackRemoveFromCart = useCallback((productId, productName, quantity = 1) => {
    trackEvent('remove_from_cart', {
      product_id: productId,
      product_name: productName,
      quantity: quantity,
      page: location.pathname
    });
  }, [trackEvent, location.pathname]);

  const trackCheckoutStep = useCallback((step, stepNumber) => {
    trackEvent('checkout_step', {
      step: step,
      step_number: stepNumber,
      page: location.pathname
    });
  }, [trackEvent, location.pathname]);

  const trackContactAction = useCallback((action, method = 'form') => {
    trackEvent('contact_action', {
      action: action,
      method: method,
      page: location.pathname
    });
  }, [trackEvent, location.pathname]);

  const trackSocialShare = useCallback((platform, content = null) => {
    trackEvent('social_share', {
      platform: platform,
      content: content,
      page: location.pathname
    });
  }, [trackEvent, location.pathname]);

  const trackDownload = useCallback((fileType, fileName) => {
    trackEvent('download', {
      file_type: fileType,
      file_name: fileName,
      page: location.pathname
    });
  }, [trackEvent, location.pathname]);

  const trackVideoPlay = useCallback((videoTitle, videoDuration = 0) => {
    trackEvent('video_play', {
      video_title: videoTitle,
      video_duration: videoDuration,
      page: location.pathname
    });
  }, [trackEvent, location.pathname]);

  return {
    trackEvent,
    trackLogin,
    trackTransaction,
    trackPurchase,
    trackButtonClick,
    trackFormSubmission,
    trackSearch,
    trackProductView,
    trackAddToCart,
    trackRemoveFromCart,
    trackCheckoutStep,
    trackContactAction,
    trackSocialShare,
    trackDownload,
    trackVideoPlay,
    clientId
  };
};

export default useGoogleAnalytics;
